/* eslint-disable @typescript-eslint/no-unused-vars */
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Checkbox, Icon, IconButton, LinearProgress, Pagination, Paper,
  Snackbar, SnackbarCloseReason, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, Chip, Box, Typography,
  useTheme, alpha
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDeFinanceiro } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import dayjs from 'dayjs';
import { ContasReceberService, IListagemContasReceber, IContaConsolidadaFilho } from '../../../shared/services/api/Financeiro/ContasReceber';
import { FerramentasDaListagemFinanceiro, LinhasEsqueletoTabela } from '../../../shared/components';
import { DialoDesconto } from './DialogDesconto';
import { DialoBaixa } from './DialogBaixa';
import { v4 as uuidv4 } from 'uuid';
import { DialoBaixaCheque } from './DialogBaixaCheque';
import { DialogJuntarContas } from './DialogJuntarContas';
import PDFReceber from './ImpressaoReceber';
import { IComprovanteBaixaDados } from './ComprovanteBaixa';
import { DialogVisualizarComprovante } from './DialogVisualizarComprovante';
import { EmpresaService } from '../../../shared/services/api/cadastros/Empresa';
import { CelulaListaMobile, LinhaListagemMobile, moedaMobile, useCelular } from '../../../shared/components/listagem-mobile';

// Paleta específica da identificação visual de "conta consolidada" — ciano
// bem suave, para não competir com o vermelho (reservado só pra "Vencida",
// ver isVencida) nem com a cor de seleção (marcado). Consolidado é sobre
// ESTRUTURA do registro, não sobre situação financeira — as duas coisas são
// independentes (uma conta pode ser consolidada E vencida ao mesmo tempo).
const CONSOLIDADO_BG = 'linear-gradient(90deg, rgba(0,226,244,0.09) 0%, rgba(0,226,244,0.025) 45%, rgba(255,255,255,0) 100%)';
const CONSOLIDADO_BORDA = '3px solid #00e2f4';
// textoPrincipal saiu daqui — era um hex fixo (#001e27, quase preto),
// pensado só pro tema claro; no escuro ficava texto escuro sobre fundo
// escuro, quase ilegível (cabeçalho e colunas Valor/Receber). Usa
// theme.palette.text.primary direto no sx de cada célula (ver mais abaixo),
// que já é theme-aware — só não dá pra ficar aqui porque CORES é módulo
// (fora do componente), sem acesso ao hook useTheme().
const CORES = {
  jurosAcrescimo: '#d97706',
  recebido: '#059669',
  desconto: '#2563eb',
  vencido: '#e11d48',
};

// Vencida é sobre PRAZO, não sobre situação — uma conta já paga (status
// = true) nunca pode aparecer com o selo "Vencida", mesmo que o
// vencimento dela já tenha passado. Ver badge "Conta Paga" na linha.
const isVencida = (row: IListagemContasReceber) => {
  return !row.status && dayjs(row.vencimento).isBefore(dayjs(), 'day');
};

// Linha isolada + React.memo: marcar uma conta só re-renderiza as linhas
// cujas props mudaram (a clicada e, no Vencimento, a desmarcada), não a
// tabela inteira — com muitas contas no período o clique ficava lento.
interface ILinhaContaReceberProps {
  // Celular: a linha vira um cartão (LinhaListagemMobile) no lugar das colunas.
  celular?: boolean;
  row: IListagemContasReceber;
  expandida: boolean;
  carregandoFilhos: boolean;
  filhos?: IContaConsolidadaFilho[];
  carregandoComprovante: boolean;
  mostrarComprovante: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditar: (idrec: string) => void;
  onToggleExpandir: (idrec: string) => void;
  onImprimirComprovante: (row: IListagemContasReceber) => void;
}

const LinhaContaReceber = memo(({
  row, expandida, carregandoFilhos, filhos, carregandoComprovante, mostrarComprovante,
  onChange, onEditar, onToggleExpandir, onImprimirComprovante, celular,
}: ILinhaContaReceberProps) => {
  const theme = useTheme();
  const vencida = isVencida(row);
  const paga = !!row.status;
  const consolidado = Number(row.qtdtitulos) > 0;
  // Parcela (2ª em diante) de uma consolidação dividida em N
  // parcelas — não é o container (não tem "filhos" pra expandir
  // nem contagem de títulos), mas deve ser pintada como
  // "Consolidado" igual o container (ver GetAll.ts, backend).
  const parcelaConsolidada = !!row.parcelaconsolidada;

  // Celular: cartão com o cliente, vencimento e valor a receber; marcar pela
  // caixinha (mesmo id/onChange da tabela) e, nas recebidas, o comprovante.
  // Os títulos de uma conta consolidada só aparecem na tabela (computador).
  if (celular) {
    return (
      <CelulaListaMobile>
        <LinhaListagemMobile
          avatar={row.parceiros || '?'}
          cor={vencida ? CORES.vencido : paga ? CORES.recebido : consolidado || parcelaConsolidada ? '#00b8c6' : undefined}
          titulo={row.parceiros}
          detalhes={[
            `Vence ${dayjs(row.vencimento).format('DD/MM/YY')}`,
            row.parcela ? `Parc. ${row.parcela}` : null,
            row.numero ? `Nº ${row.numero}` : null,
            consolidado ? `${row.qtdtitulos} título${Number(row.qtdtitulos) > 1 ? 's' : ''}` : null,
          ]}
          valor={moedaMobile(row.vreceber)}
          corValor={vencida ? CORES.vencido : undefined}
          etiqueta={paga ? { rotulo: 'Paga', cor: 'success' } : vencida ? { rotulo: 'Vencida', cor: 'error' } : consolidado || parcelaConsolidada ? { rotulo: 'Consolidado', cor: 'info' } : undefined}
          destaque={!!row.marcado}
          onClick={() => onEditar(row.idrec)}
          acoes={(
            <>
              {mostrarComprovante && (
                <IconButton size="small" disabled={carregandoComprovante} onClick={() => onImprimirComprovante(row)}>
                  <Icon sx={{ fontSize: '18px', color: CORES.recebido }}>print</Icon>
                </IconButton>
              )}
              <Checkbox checked={!!row.marcado} id={row.idrec} onChange={onChange} color="primary" size="small" />
            </>
          )}
        />
      </CelulaListaMobile>
    );
  }

  if (!consolidado && !parcelaConsolidada) {
    return (
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          backgroundColor: row.marcado ? alpha(theme.palette.primary.light, 0.08) : 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0,226,244,0.035)'
          }
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '5%', px: 0.5 }}>
          <Checkbox
            checked={!!row.marcado}
            id={row.idrec}
            onChange={onChange}
            color="primary"
            size="small"
          />
        </TableCell>
        <TableCell align='center' sx={{ px: 0.5 }}>
          {dayjs(row.emissao).format('DD/MM/YY')}
        </TableCell>
        <TableCell align='center' sx={{ px: 0.5 }}>
          <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            {dayjs(row.vencimento).format('DD/MM/YY')}
            {paga && (
              <Chip
                label="Conta Paga"
                size="small"
                sx={{
                  mt: 0.3, height: '16px', fontSize: '0.55rem', minWidth: '45px', fontWeight: 600,
                  backgroundColor: 'rgba(5,150,105,0.10)', color: CORES.recebido, border: '1px solid rgba(5,150,105,0.18)',
                }}
              />
            )}
            {vencida && (
              <Chip
                label="Vencida"
                size="small"
                sx={{
                  mt: 0.3, height: '16px', fontSize: '0.55rem', minWidth: '45px', fontWeight: 600,
                  backgroundColor: 'rgba(225,29,72,0.10)', color: CORES.vencido, border: '1px solid rgba(225,29,72,0.18)',
                }}
              />
            )}
            {paga && row.datarecebimento && (
              <Typography component="span" sx={{ fontSize: '0.55rem', color: 'text.secondary', mt: 0.2 }}>
                {dayjs(row.datarecebimento).format('DD/MM/YY')}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align='left' sx={{ fontFamily: 'monospace', px: 0.5 }}>
          {row.parceiros}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', px: 0.5 }}>
          {row.numero}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', fontWeight: 700, color: theme.palette.text.primary, px: 0.5 }}>
          {Number(row.venda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', px: 0.5 }}>
          <Box display="flex" alignItems="center" justifyContent="flex-end" flexDirection="column">
            {Number(row.vparcela).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {row.parcela && (
              <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.1 }}>
                {row.parcela}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', color: CORES.jurosAcrescimo, px: 0.5 }}>
          {Number(row.acrescimo).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', color: CORES.recebido, px: 0.5 }}>
          {Number(row.vrecebido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', color: CORES.desconto, px: 0.5 }}>
          {Number(row.desconto).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', fontWeight: 700, color: vencida ? CORES.vencido : theme.palette.text.primary, px: 0.5 }}>
          {Number(row.vreceber).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='center' sx={{ px: 0.5 }}>
          <IconButton
            size='small'
            onClick={() => onEditar(row.idrec)}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              padding: '3px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.15)
              }
            }}
          >
            <Icon sx={{ fontSize: '16px' }}>edit</Icon>
          </IconButton>
          {mostrarComprovante && (
            <IconButton
              size='small'
              disabled={carregandoComprovante}
              onClick={(event) => {
                event.stopPropagation();
                onImprimirComprovante(row);
              }}
              sx={{
                backgroundColor: alpha(CORES.recebido, 0.08),
                padding: '3px',
                ml: 0.5,
                '&:hover': {
                  backgroundColor: alpha(CORES.recebido, 0.15)
                }
              }}
            >
              <Icon sx={{ fontSize: '16px', color: CORES.recebido }}>print</Icon>
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    );
  }

  // Linha de uma conta consolidada (ver ConsolidacaoReceber.ts,
  // backend) — tanto o "container" (qtdtitulos > 0, com o botão de
  // expandir/mostrar as contas antigas juntadas) quanto cada uma das
  // demais parcelas do mesmo grupo (parcelaconsolidada = true, sem
  // botão de expandir e com o chip mostrando "Parcela i/N" no lugar
  // da contagem de títulos). Emissão e Parcela não fazem sentido pra
  // um registro que junta várias origens; Vencimento continua
  // mostrado (com o badge "Vencida" se for o caso), porque
  // consolidado e vencido são informações independentes — uma conta
  // pode ser as duas coisas ao mesmo tempo.
  return (
    <Fragment>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          background: row.marcado ? 'rgba(0,226,244,0.14)' : CONSOLIDADO_BG,
          '&:hover': { backgroundColor: 'rgba(0,226,244,0.06)' }
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '5%', px: 0.5, borderLeft: CONSOLIDADO_BORDA }}>
          <Checkbox
            checked={!!row.marcado}
            id={row.idrec}
            onChange={onChange}
            color="primary"
            size="small"
          />
        </TableCell>
        <TableCell align='center' sx={{ px: 0.5 }}>
          {dayjs(row.emissao).format('DD/MM/YY')}
        </TableCell>
        <TableCell align='center' sx={{ px: 0.5 }}>
          <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            {dayjs(row.vencimento).format('DD/MM/YY')}
            {paga && (
              <Chip
                label="Conta Paga"
                size="small"
                sx={{
                  mt: 0.3, height: '16px', fontSize: '0.55rem', minWidth: '45px', fontWeight: 600,
                  backgroundColor: 'rgba(5,150,105,0.10)', color: CORES.recebido, border: '1px solid rgba(5,150,105,0.18)',
                }}
              />
            )}
            {vencida && (
              <Chip
                label="Vencida"
                size="small"
                sx={{
                  mt: 0.3, height: '16px', fontSize: '0.55rem', minWidth: '45px', fontWeight: 600,
                  backgroundColor: 'rgba(225,29,72,0.10)', color: CORES.vencido, border: '1px solid rgba(225,29,72,0.18)',
                }}
              />
            )}
            {paga && row.datarecebimento && (
              <Typography component="span" sx={{ fontSize: '0.55rem', color: 'text.secondary', mt: 0.2 }}>
                {dayjs(row.datarecebimento).format('DD/MM/YY')}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align='left' sx={{ px: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            <Icon sx={{ fontSize: '17px', color: '#00b8cc' }}>workspaces</Icon>
            <Typography component="span" sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#00838f' }}>
              Consolidado
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.84rem', color: 'text.secondary' }}>
              · {row.parceiros}
            </Typography>
            <Chip
              label={consolidado
                ? `${row.qtdtitulos} título${Number(row.qtdtitulos) > 1 ? 's' : ''}`
                : `Parcela ${row.parcela}`}
              size="small"
              sx={{
                height: '18px', fontSize: '0.65rem', fontWeight: 600,
                backgroundColor: 'rgba(0,226,244,0.12)', color: '#007686', border: '1px solid rgba(0,184,204,0.25)',
              }}
            />
          </Box>
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', px: 0.5 }}>
          {row.numero}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', fontWeight: 700, color: theme.palette.text.primary, px: 0.5 }}>
          {Number(row.venda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', px: 0.5 }}>
          {Number(row.vparcela).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', color: CORES.jurosAcrescimo, px: 0.5 }}>
          {Number(row.acrescimo).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', color: CORES.recebido, px: 0.5 }}>
          {Number(row.vrecebido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', color: CORES.desconto, px: 0.5 }}>
          {Number(row.desconto).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='right' sx={{ fontFamily: 'monospace', fontWeight: 700, color: vencida ? CORES.vencido : theme.palette.text.primary, px: 0.5 }}>
          {Number(row.vreceber).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </TableCell>
        <TableCell align='center' sx={{ px: 0.5 }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            <IconButton
              size='small'
              onClick={() => onEditar(row.idrec)}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                padding: '3px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15)
                }
              }}
            >
              <Icon sx={{ fontSize: '16px' }}>edit</Icon>
            </IconButton>
            {consolidado && (
              <IconButton
                size='small'
                onClick={() => onToggleExpandir(row.idrec)}
                sx={{
                  backgroundColor: 'rgba(0,226,244,0.10)',
                  padding: '3px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,226,244,0.18)'
                  }
                }}
              >
                <Icon sx={{ fontSize: '16px', color: '#007686' }}>{expandida ? 'expand_less' : 'expand_more'}</Icon>
              </IconButton>
            )}
          </Box>
        </TableCell>
      </TableRow>
      {expandida && carregandoFilhos && (
        <TableRow>
          <TableCell colSpan={12} sx={{ py: 0 }}>
            <LinearProgress variant='indeterminate' />
          </TableCell>
        </TableRow>
      )}
      {expandida && !carregandoFilhos && (filhos || []).map((filho, index) => (
        <TableRow key={`${row.idrec}-filho-${index}`} sx={{ backgroundColor: 'rgba(0,30,39,0.015)' }}>
          <TableCell padding="checkbox" sx={{ width: '5%', px: 0.5 }} />
          <TableCell align='center' sx={{ fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
            {dayjs(filho.emissao).format('DD/MM/YY')}
          </TableCell>
          <TableCell align='center' sx={{ fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
            {dayjs(filho.vencimento).format('DD/MM/YY')}
          </TableCell>
          <TableCell align='left' sx={{ pl: '24px', py: 0.5 }}>
            <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
              <Icon sx={{ fontSize: '14px' }}>subdirectory_arrow_right</Icon>
              <Typography component="span" sx={{ fontSize: '0.78rem' }}>
                {filho.parcela ? `Parcela ${filho.parcela}` : 'título juntado'}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
            {filho.numero || '—'}
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
            {Number(filho.venda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
            {Number(filho.vparcela).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: CORES.jurosAcrescimo, px: 0.5 }}>
            {Number(filho.acrescimo).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: CORES.recebido, px: 0.5 }}>
            {Number(filho.vrecebido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: CORES.desconto, px: 0.5 }}>
            {Number(filho.desconto).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
          <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', px: 0.5 }}>
            {Number(filho.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
          <TableCell sx={{ px: 0.5 }} />
        </TableRow>
      ))}
    </Fragment>
  );
});
LinhaContaReceber.displayName = 'LinhaContaReceber';

export const ListagemContasReceber: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IListagemContasReceber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hoje = dayjs();
  const primeiroDiaMes = dayjs().startOf('month');

  const [dataInicial, setDataInicial] = useState(primeiroDiaMes.format('YYYY-MM-DD'));
  const [dataFinal, setDataFinal] = useState(hoje.format('YYYY-MM-DD'));


  const [totalCount, setTotalCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [selecionado, setSelecionado] = useState(0);
  const [open, setOpen] = useState(false);
  const [openBaixa, setOpenBaixa] = useState(false);
  const [openCheque, setOpenCheque] = useState(false);
  const [openJuntar, setOpenJuntar] = useState(false);
  const [linhasExpandidas, setLinhasExpandidas] = useState<Set<string>>(new Set());
  const [filhosPorConta, setFilhosPorConta] = useState<Record<string, IContaConsolidadaFilho[]>>({});
  const [carregandoFilhos, setCarregandoFilhos] = useState<Set<string>>(new Set());
  // Comprovante por conta (estilo carnê, com assinatura) — clicado direto
  // numa linha já paga, no modo "recebidas". Mesmo fluxo de
  // DetalhesDeReceber.tsx/handleReimprimir, só que a partir da listagem.
  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteBaixaDados | null>(null);
  const [openComprovante, setOpenComprovante] = useState(false);
  const [carregandoComprovante, setCarregandoComprovante] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };
  // Estável entre renders (antes era um uuidv4() novo a cada render, o que
  // disparava o efeito de `value` dos diálogos de baixa em todo clique) —
  // gera um novo só ao abrir a baixa/cheque.
  const [guuid, setGuuid] = useState(() => uuidv4());

  const handleEditar = useCallback((idrec: string) => {
    navigate(`/contasreceber/${idrec}`);
  }, [navigate]);

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const basicselect = useMemo(() => {
    return searchParams.get('basicselect') || 'parceiro';
  }, [searchParams]);

  const basicstatus = useMemo(() => {
    return searchParams.get('basicstatus') || 'false';
  }, [searchParams]);

  const parceiroselect = useMemo(() => {
    return searchParams.get('parceiroselect') || '';
  }, [searchParams]);

  const parceiro = useMemo(() => {
    return searchParams.get('parceiro') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  const mensagemVazia = useMemo(() => {
  if (basicselect === 'parceiro') {
    if (!parceiroselect) {
      return 'Pesquise um parceiro';
    }
    return 'Nenhum registro encontrado para este parceiro';
  }

  if (basicselect === 'data') {
    return 'Não temos nenhum registro entre essas datas';
  }

  if (basicselect === 'recebidas') {
    return 'Nenhuma conta recebida nesse período';
  }

  if (busca !== '') {
    return 'Não temos nenhum registro com esse número';
  }

  return 'Nenhum registro encontrado';
}, [basicselect, parceiroselect, busca]);

  useEffect(() => {
    const urlDataI = searchParams.get('datai') || dataInicial;
    const urlDataF = searchParams.get('dataf') || dataFinal;

    setDataInicial(urlDataI);
    setDataFinal(urlDataF);
  }, [searchParams]);
  useEffect(() => {

    if (basicselect === 'data' || basicselect === 'recebidas') {
      const hoje = dayjs();
      const primeiroDiaMes = dayjs().startOf('month');

      setDataInicial(primeiroDiaMes.format('YYYY-MM-DD'));
      setDataFinal(hoje.format('YYYY-MM-DD'));
      setSearchParams({
        basicselect,
        datai: primeiroDiaMes.format('YYYY-MM-DD'),
        dataf: hoje.format('YYYY-MM-DD'),
        pagina: '1',
        // Em 'recebidas' o parceiro é um filtro opcional combinado com a
        // data (ver mostrarAutoComplete abaixo) — preserva o que já estava
        // na URL, senão remontar a listagem com basicselect='recebidas' já
        // na URL (ex.: voltando da tela de "Novo") apagava o parceiro
        // pesquisado nesse mesmo instante. Em 'data' não existe esse campo,
        // então fica vazio à toa, sem efeito.
        ...(basicselect === 'recebidas' ? { parceiroselect, parceiro } : {}),
      });
    }

    setRows([]);
  }, [basicselect]);
  useEffect(() => {
    if (basicselect === 'data') {
      setIsLoading(true);

      ContasReceberService.getAll(pagina, parceiroselect, 'data', dataInicial, dataFinal)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotal(result.total);
            // Vencimento é seleção única — começa sempre sem nada marcado.
            setSelecionado(0);
            setRows(result.data.map(row => ({ ...row, marcado: false })));
          }
        });
    }
  }, [basicselect, dataInicial, dataFinal, pagina,]);

  // Contas já baixadas, filtradas pela data em que o recebimento aconteceu
  // (não pelo vencimento) — ver GetAll.ts (tipo 'recebidas'), backend.
  // `parceiroselect` opcional: sem ele, traz as pagas de todos os clientes
  // no período; com ele, só as desse cliente — mesmo AutoCompleteParceiro já
  // usado no modo 'parceiro' (ver mostrarAutoComplete mais abaixo).
  useEffect(() => {
    if (basicselect === 'recebidas') {
      setIsLoading(true);

      ContasReceberService.getAll(pagina, parceiroselect, 'recebidas', dataInicial, dataFinal)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotal(result.total);
            setSelecionado(result.selecionado);
            setRows(result.data);
          }
        });
    }
  }, [basicselect, dataInicial, dataFinal, pagina, parceiroselect]);

  useEffect(() => {
    if (parceiroselect !== '') {
      setNome(parceiro);
      ContasReceberService.getAll(pagina, parceiroselect, 'parceiro')
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotal(result.total);
            setSelecionado(result.selecionado);
            setRows(result.data);
          }
        });
    }
  }, [basicstatus, parceiroselect, pagina]);

  useEffect(() => {
    setIsLoading(true);
    if (busca !== '') {
      debounce(() => {
        ContasReceberService.getAll(pagina, busca, 'numero')
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            }
            else if (!result.data || result.data.length === 0) {
              // quando não encontrar nada
              setRows([]);
              setTotalCount(0);
              setTotal(0);
              setSelecionado(0);

            } else {
              // quando encontrar registros
              setTotalCount(1);
              setTotal(result.total);
              setSelecionado(result.selecionado);
              setRows(result.data);
            }
          });
      });
    }
    setIsLoading(false)
    setRows([]);

  }, [busca]);

  // Forma funcional de setRows (prevRows => ...), não fechando sobre `rows`
  // direto — com uma listagem grande (até 300 linhas por página aqui), um
  // clique rápido logo depois de outro podia pegar o `rows` de um render
  // anterior, fazendo o clique seguinte "não colar" (dava a impressão de
  // lentidão/de não desmarcar, mesmo o estado final ficando certo depois).
  // useCallback também evita recriar a função (e por tabela invalidar a
  // prop `onChange` de toda linha) a cada render só porque outra coisa no
  // componente mudou.
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = event.target;

    // Vencimento: uma conta por vez — marcar uma desmarca todas as outras.
    if (basicselect === 'data') {
      setRows(prevRows => {
        const index = prevRows.findIndex(row => row.idrec === id);
        if (index === -1) return prevRows;

        setSelecionado(checked ? Number(prevRows[index].vreceber) : 0);
        return prevRows.map((row, i) => {
          const marcado = checked && i === index;
          return row.marcado === marcado ? row : { ...row, marcado };
        });
      });
      return;
    }

    setRows(prevRows => {
      const index = prevRows.findIndex(row => row.idrec === id);
      if (index === -1) return prevRows;

      const updatedRow = { ...prevRows[index], marcado: checked };
      const valorNumerico = Number(updatedRow.vreceber);
      const valueToUpdate = checked ? valorNumerico : -valorNumerico;
      setSelecionado(currentSelecionado => currentSelecionado + valueToUpdate);

      return [...prevRows.slice(0, index), updatedRow, ...prevRows.slice(index + 1)];
    });
  }, [basicselect]);
  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const updatedRows = rows.map(row => ({ ...row, marcado: checked }));
    setRows(updatedRows);
    if (checked) {
      const totalSelecionado = updatedRows.reduce((acc, row) => {
        const valorNumerico = Number(row.vreceber);
        return acc + valorNumerico;
      }, 0);
      setSelecionado(totalSelecionado);
    } else {
      setSelecionado(0);
    }
  };

  const handleClose = (newValue?: string) => {
    setOpen(false);
    if (newValue) {
      // setValue(newValue); 
    }
  };

  // Aba Vencimento: a baixa deixou só a conta marcada na lista, então ao
  // fechar o diálogo (baixando ou cancelando) recarrega o período inteiro.
  const recarregarVencimento = () => {
    setIsLoading(true);
    ContasReceberService.getAll(pagina, parceiroselect, 'data', dataInicial, dataFinal)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setTotalCount(result.totalCount);
          setTotal(result.total);
          setSelecionado(0);
          setRows(result.data.map(row => ({ ...row, marcado: false })));
        }
      });
  };

  const handleCloseBaixa = (newValue?: string) => {
    setOpenBaixa(false);
    if (basicselect === 'data') {
      recarregarVencimento();
      return;
    }
    if (newValue === 'true') {
      ContasReceberService.getAll(pagina, parceiroselect, 'parceiro')
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotal(result.total);
            setSelecionado(result.selecionado);
            setRows(result.data);
          }
        });
    }
  };

  const handleCloseCheque = (newValue?: string) => {
    setOpenCheque(false);
    if (basicselect === 'data') {
      recarregarVencimento();
      return;
    }
    if (newValue === 'true') {
      ContasReceberService.getAll(pagina, parceiroselect, 'parceiro')
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotal(result.total);
            setSelecionado(result.selecionado);
            setRows(result.data);
          }
        });
    }
  };
  const handleCloseJuntar = (newValue?: string) => {
    setOpenJuntar(false);
    if (newValue === 'true') {
      ContasReceberService.getAll(pagina, parceiroselect, 'parceiro')
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setTotal(result.total);
            setSelecionado(result.selecionado);
            setRows(result.data);
          }
        });
    }
  };
  const handleDesconto = () => {
    if (totalCount != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      setRows(filteredList);
      setOpen(true);
    }
  };
  // Na aba Vencimento a baixa é uma conta por vez (ver handleChange).
  const vencimentoSemSelecaoUnica = (marcadas: IListagemContasReceber[]) => {
    if (basicselect !== 'data' || marcadas.length === 1) return false;
    setTms('warning');
    setMsg('Selecione uma conta para dar baixa');
    setOpenmsg(true);
    return true;
  };
  const handleBaixa = () => {
    if (totalCount != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      if (vencimentoSemSelecaoUnica(filteredList)) return;
      setRows(filteredList);
      setGuuid(uuidv4());
      setOpenBaixa(true);
    }
  };
  const handleCheque = () => {
    if (totalCount != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      if (vencimentoSemSelecaoUnica(filteredList)) return;
      setRows(filteredList);
      setGuuid(uuidv4());
      setOpenCheque(true);
    }
  };
  const handleJuntar = () => {
    const filteredList = rows.filter((item) => item.marcado === true);
    if (filteredList.length < 2) {
      setTms('warning');
      setMsg('Selecione ao menos duas contas para juntar');
      setOpenmsg(true);
      return;
    }
    setRows(filteredList);
    setOpenJuntar(true);
  };

  const handleSave = async () => {
    if (totalCount != 0) {
      setIsLoading(true);
      const marcados = rows.map(listItem => ({
        idrec: listItem.idrec,
        marcado: listItem.marcado,
        desconto: listItem.desconto,
        vreceber: listItem.vreceber,
      }));
      try {
        const result = await ContasReceberService.Marcados(marcados);
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        }
      } catch (error) {
        setIsLoading(false);
        setTms('error');
        setMsg('Ocorreu um erro ao processar sua solicitação.');
        setOpenmsg(true);
      }
    }
  };
  const handleDesc = (dados?: IListagemContasReceber[]) => {
    if (dados) {
      setRows(dados);
    }
  };
  const handletotal = (valor?: number) => {
    if (valor) {
      setSelecionado(valor);
    }
  };
  const handlePrint = () => {
    setIsLoading(true);
    if (selecionado != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      PDFReceber(filteredList, total, nome);
    } else {
      setTms('error');
      setMsg('Selecione uma Conta');
      setOpenmsg(true);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  // Comprovante de uma conta já recebida (estilo carnê, com assinatura) —
  // precisa de getRecebidos (forma de pagamento/conta bancária, que a
  // listagem não tem) e da empresa emitente, igual DetalhesDeReceber.tsx.
  const handleImprimirComprovante = useCallback(async (row: IListagemContasReceber) => {
    setCarregandoComprovante(row.idrec);
    const [historicoResult, empresaResult] = await Promise.all([
      ContasReceberService.getRecebidos(row.idrec),
      EmpresaService.getById(),
    ]);
    setCarregandoComprovante(null);

    if (historicoResult instanceof Error || historicoResult.length === 0) {
      setTms('error');
      setMsg(historicoResult instanceof Error ? historicoResult.message : 'Nenhum recebimento encontrado para este título.');
      setOpenmsg(true);
      return;
    }
    // Título pode ter mais de uma baixa (parcial + final) — usa a mais
    // recente, mesmo critério do `datarecebimento` já mostrado na linha.
    const item = historicoResult[historicoResult.length - 1];
    const empresa = !(empresaResult instanceof Error) ? empresaResult : undefined;

    setComprovanteDados({
      parceiro: item.nomeParceiro || row.parceiros || '-',
      contaNome: item.nomeConta || '-',
      datalan: item.dataoperacao,
      contas: [{ numero: item.numero || row.numero || '', valor: Number(item.valor), historico: row.historico }],
      formasPagamento: item.formasPagamento.length > 0
        ? item.formasPagamento.map((f) => ({ nome: f.historico, valor: Number(f.valor) }))
        : [{ nome: 'Recebimento', valor: Number(item.valor) }],
      totalBaixado: Number(item.valor),
      desconto: 0,
      acrescimo: 0,
      empresaFantasia: empresa?.fantasia,
      empresaRazaoSocial: empresa?.nome,
    });
    setOpenComprovante(true);
  }, []);

  // Expande/recolhe as parcelas de uma conta consolidada — busca os filhos
  // só na primeira vez (reaproveita o mesmo getById que já alimenta a tela
  // de detalhe, sem endpoint novo) e guarda em cache pelo idrec.
  const handleToggleExpandir = useCallback(async (idrec: string) => {
    setLinhasExpandidas((atual) => {
      const novo = new Set(atual);
      if (novo.has(idrec)) novo.delete(idrec); else novo.add(idrec);
      return novo;
    });
    if (filhosPorConta[idrec]) return;

    setCarregandoFilhos((atual) => new Set(atual).add(idrec));
    const resultado = await ContasReceberService.getById(idrec);
    setCarregandoFilhos((atual) => {
      const novo = new Set(atual);
      novo.delete(idrec);
      return novo;
    });
    if (resultado instanceof Error) {
      setTms('error');
      setMsg(resultado.message);
      setOpenmsg(true);
      return;
    }
    setFilhosPorConta((atual) => ({ ...atual, [idrec]: resultado.filhos || [] }));
  }, [filhosPorConta]);

  return (
    <LayoutBaseDeFinanceiro
      titulo='Contas a Receber'
      labelSelecionado='Selecionado'
      labeltotal='Total Receber'
      total={total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      selecionado={selecionado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      barraDeFerramentas={
        <FerramentasDaListagemFinanceiro
          mostrarInputBusca={basicselect === 'r.numerodoc'}
          mostrarInputData={basicselect === 'data' || basicselect === 'recebidas'}
          // Também em 'recebidas' — permite restringir as contas já pagas
          // do período a um cliente específico (opcional, sem ele mostra de
          // todos os clientes).
          mostrarAutoComplete={basicselect === 'parceiro' || basicselect === 'recebidas'}
          mostrarInputBasicSelect
          mostrarOpcaoRecebidas
          mostrarBotaoCheque={true}
          mostrarBotaoJuntar={basicselect === 'parceiro' && basicstatus === 'false'}
          dataInicial={dataInicial}
          dataFinal={dataFinal}
          textSelectFalse='Receber'
          textSelectTrue='Recebidas'
          textoDaBusca={busca}
          textoDoSelect={basicselect}
          textoDoStatus={basicstatus}
          // Vencimento libera a Baixa, uma conta por vez (ver handleChange).
          disbilitarBaixar={basicstatus === 'true' || basicselect === 'recebidas'}
          disbilitarDesconto={basicstatus === 'true' || basicselect === 'recebidas'}
          disbilitarSalvar={basicstatus === 'true' || basicselect === 'recebidas'}
          disbilitarCheque={basicstatus === 'true' || basicselect === 'recebidas'}
          disbilitarImprimir={
            (basicselect !== 'parceiro' && basicselect !== 'recebidas') || basicstatus === 'true'
          }
          textoBotaoNovo='Adicionar'
          // Leva o parceiro filtrado na listagem (basicselect==='parceiro') já
          // pré-selecionado pra tela de novo lançamento — ver DetalhesDeReceber.tsx.
          aoClicarEmNovo={() => navigate('/contasreceber/novo', parceiroselect ? { state: { idparceiro: parceiroselect } } : undefined)}
          aoClicarEmDesconto={handleDesconto}
          aoClicarEmImpressao={() => handlePrint()}
          aoClicarEmSalvar={handleSave}
          aoClicarEmBaixa={handleBaixa}
          aoClicarEmCheque={handleCheque}
          aoClicarEmJuntar={handleJuntar}
          aoMudarDataInicial={(data) =>
            setSearchParams(
              {
                basicselect,
                datai: data,
                dataf: dataFinal,
                pagina: '1'
              },
              { replace: true }
            )
          }

          aoMudarDataFinal={(data) =>
            setSearchParams(
              {
                basicselect,
                datai: dataInicial,
                dataf: data,
                pagina: '1'
              },
              { replace: true }
            )
          }

          aoMudarTextoDoParceiro={(texto, texto1) => setSearchParams({ busca: '', basicstatus: basicstatus, parceiroselect: texto, parceiro: texto1, pagina: '1' }, { replace: true })}
          aoMudarTextoDoStatus={texto => setSearchParams({ busca: '', parceiroselect: parceiroselect, parceiro: parceiro, pagina: '1', basicstatus: texto }, { replace: true })}
          aoMudarTextoDeBusca={texto => setSearchParams({ busca: texto, basicselect: basicselect })}
          aoMudarTextoDoSelect={textoselect => setSearchParams({ busca: '', basicselect: textoselect, parceiroselect: parceiroselect, parceiro: parceiro, pagina: '1' }, { replace: true })}
          // Preserva o parceiro filtrado visualmente no autocomplete de busca (ver
          // AutoCompleteParceiroListagem.tsx) — sem isso o campo sempre voltava em
          // branco, mesmo com a listagem já filtrada por esse parceiro.
          valorInicialParceiro={parceiroselect ? { idparceiro: parceiroselect, nome: parceiro } : null}
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mx: 1,
          mb: 1,
          borderRadius: 2,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[2]
          },
          width: '98%' // Garante que ocupa 100% da largura disponível
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: listaCelular ? 0 : 1000,
            tableLayout: 'auto',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid rgba(0,30,39,0.07)',
              fontSize: '0.84rem',
            },
          }}
        >
          {!listaCelular && (
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
              <TableCell padding="checkbox" sx={{ width: '5%', px: 0.5 }}>
                {basicselect !== 'data' && (
                  <Checkbox
                    checked={rows.length > 0 && rows.every(row => row.marcado)}
                    onChange={handleSelectAllChange}
                    color="primary"
                    size="small"
                  />
                )}
              </TableCell>
              <TableCell align='center' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Emissão</TableCell>
              <TableCell align='center' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '10%', px: 0.5 }}>Vencimento</TableCell>
              <TableCell align='left' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '*', px: 0.5 }}>Parceiros</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '5%', px: 0.5 }}>Número</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Valor</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Parcela</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Juros/Acré.</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Recebido</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Desconto</TableCell>
              <TableCell align='right' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '8%', px: 0.5 }}>Receber</TableCell>
              <TableCell align='center' sx={{ fontWeight: 700, color: theme.palette.text.primary, width: '9%', px: 0.5 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          )}
          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={[
                  { variant: 'rounded', width: 18, height: 18, align: 'center' },
                  { width: '55%', align: 'center' },
                  { width: '55%', align: 'center' },
                  { width: '80%' },
                  { width: '50%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '60%', align: 'right' },
                  { variant: 'circular', width: 24, height: 24, align: 'center' },
                ]}
              />
            </TableBody>
          )}
          <TableBody>
            {rows.map(row => (
              <LinhaContaReceber
                key={row.idrec}
                row={row}
                celular={listaCelular}
                expandida={linhasExpandidas.has(row.idrec)}
                carregandoFilhos={carregandoFilhos.has(row.idrec)}
                filhos={filhosPorConta[row.idrec]}
                carregandoComprovante={carregandoComprovante === row.idrec}
                mostrarComprovante={basicselect === 'recebidas'}
                onChange={handleChange}
                onEditar={handleEditar}
                onToggleExpandir={handleToggleExpandir}
                onImprimirComprovante={handleImprimirComprovante}
              />
            ))}
          </TableBody>
          {rows.length === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Icon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}>receipt</Icon>
                  <Typography variant="h6" color="text.secondary">
                   {mensagemVazia}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={12}>
                  <LinearProgress variant='indeterminate' />
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
      <DialoDesconto
        open={open}
        onClose={handleClose}
        dados={rows}
        onDados={handleDesc}
        ontotal={handletotal}
        vselecionado={selecionado}
      />
      <DialoBaixa
        value={guuid}
        open={openBaixa}
        onClose={handleCloseBaixa}
        dados={rows}
        onDados={handleDesc}
        ontotal={handletotal}
        vselecionado={selecionado}
      />
      <DialoBaixaCheque
        value={guuid}
        open={openCheque}
        onClose={handleCloseCheque}
        dados={rows}
        onDados={handleDesc}
        ontotal={handletotal}
        vselecionado={selecionado}
      />
      <DialogJuntarContas
        open={openJuntar}
        onClose={handleCloseJuntar}
        dados={rows}
        vselecionado={selecionado}
      />
      <DialogVisualizarComprovante
        open={openComprovante}
        dados={comprovanteDados}
        onClose={() => setOpenComprovante(false)}
      />
    </LayoutBaseDeFinanceiro>
  );
};